/**
 * Small shared components used by more than one of the three interfaces.
 *
 * §29.2 governs all of them: sufficient contrast, screen-reader labels, no
 * reliance on color alone, and text labels for correct, incorrect, and
 * submitted states.
 */

import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { GradingOutcome } from "@tq/domain";
import { qrSvg } from "./qr.js";

export interface OutcomeBadgeProps {
  outcome: GradingOutcome;
  size?: "sm" | "lg";
}

const OUTCOME_TEXT: Record<
  GradingOutcome,
  { label: string; icon: string; color: string; background: string }
> = {
  correct: {
    label: "Correct",
    icon: "✓",
    color: "var(--correct)",
    background: "var(--correct-bg)",
  },
  partial: {
    label: "Partially correct",
    icon: "≈",
    color: "var(--partial)",
    background: "var(--partial-bg)",
  },
  incorrect: {
    label: "Not quite",
    icon: "✕",
    color: "var(--incorrect)",
    background: "var(--incorrect-bg)",
  },
  no_answer: { label: "No answer", icon: "—", color: "var(--none)", background: "var(--none-bg)" },
};

/** §29.2 — color is always accompanied by text and an icon. */
export function OutcomeBadge({ outcome, size = "sm" }: OutcomeBadgeProps): React.ReactElement {
  const style = OUTCOME_TEXT[outcome];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: size === "lg" ? "10px 20px" : "5px 12px",
        borderRadius: "var(--radius-pill)",
        background: style.background,
        color: style.color,
        border: `2px solid ${style.color}`,
        fontWeight: 800,
        fontSize: size === "lg" ? 20 : 14,
      }}
    >
      <span aria-hidden="true">{style.icon}</span>
      {style.label}
    </span>
  );
}

export interface ConnectionPillProps {
  state: "connecting" | "connected" | "reconnecting" | "disconnected";
}

/** §29.3 — the player device always shows one of these three, never by color alone. */
export function ConnectionPill({ state }: ConnectionPillProps): React.ReactElement {
  const label =
    state === "connected"
      ? "Connected"
      : state === "reconnecting" || state === "connecting"
        ? "Reconnecting"
        : "Disconnected";
  const color =
    state === "connected"
      ? "var(--correct)"
      : state === "disconnected"
        ? "var(--incorrect)"
        : "var(--partial)";

  return (
    <span
      role="status"
      aria-live="polite"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 12,
        fontWeight: 700,
        color,
      }}
    >
      <span
        aria-hidden="true"
        style={{ width: 8, height: 8, borderRadius: "50%", background: color, flex: "none" }}
      />
      {label}
    </span>
  );
}

export interface QrCodeProps {
  value: string;
  size?: number;
  className?: string;
}

/** F-019 — the join QR, rendered locally with no network request. */
export function QrCode({ value, size = 200, className }: QrCodeProps): React.ReactElement {
  const svg = useMemo(() => {
    try {
      return qrSvg(value, { size });
    } catch {
      return null;
    }
  }, [value, size]);

  if (!svg) {
    return (
      <div className={className} style={{ fontSize: 12, color: "var(--muted)" }}>
        {value}
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        lineHeight: 0,
        background: "#fff",
        padding: 6,
        borderRadius: 8,
        display: "inline-block",
      }}
      // The SVG is generated in this process from a URL we constructed, so
      // there is no untrusted content in it.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

export interface AvatarProps {
  /**
   * §22.6 — where the image is, resolved by the server.
   *
   * This was a photo *id* that the component pasted onto a fixed `/api/photo`
   * base. It is now a whole URL, because the component is no longer in a
   * position to know the base: in Connected Mode the image is on the object
   * store's CDN domain, and in venue mode it is a path back to the host on the
   * LAN. Composing a URL here would have meant teaching the browser bundle
   * which deployment profile it was running under, which is exactly the
   * knowledge §19.1 keeps in configuration on the server.
   */
  photoUrl: string | null;
  displayName: string;
  size?: number;
}

/**
 * §22.3 — photos are fetched from a cacheable URL. Image bytes are never
 * carried in a realtime payload.
 */
export function Avatar({ photoUrl, displayName, size = 40 }: AvatarProps): React.ReactElement {
  /**
   * §36.3 — an avatar that is not drawn yet is not a broken image.
   *
   * Generation runs behind the join, so for the first few seconds of a player's
   * session there is no URL at all — and briefly after that, one whose object
   * the CDN has not seen yet. Rendering a broken `<img>` gives the browser's
   * torn-page glyph, which reads as a fault rather than as work in progress.
   * Falling back to the initials disc is the same placeholder a player with no
   * photo already gets, and a later render with a fresh URL tries again.
   */
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [photoUrl]);

  const initials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  const shared = {
    width: size,
    height: size,
    borderRadius: "50%",
    flex: "none" as const,
    border: "2px solid rgba(255,255,255,.5)",
    objectFit: "cover" as const,
    background: "var(--surface-alt)",
  };

  if (!photoUrl || broken) {
    return (
      <span
        aria-hidden="true"
        style={{
          ...shared,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: 800,
          fontSize: size * 0.38,
          color: "var(--navy)",
        }}
      >
        {initials || "?"}
      </span>
    );
  }

  return (
    <img
      src={photoUrl}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      onError={() => setBroken(true)}
      style={shared}
    />
  );
}

export interface PanelProps {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  padding?: number;
}

export function Panel({ title, actions, children, padding }: PanelProps): React.ReactElement {
  return (
    <section className="tq-panel">
      {(title || actions) && (
        <header className="tq-panel-head">
          <h2 className="tq-panel-title">{title}</h2>
          {actions}
        </header>
      )}
      <div className="tq-panel-body" style={padding == null ? undefined : { padding }}>
        {children}
      </div>
    </section>
  );
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "danger" | "ghost";
  size?: "sm" | "md" | "lg";
}

export function Button({
  variant = "secondary",
  size = "md",
  style,
  ...rest
}: ButtonProps): React.ReactElement {
  // Every filled variant carries a white trim rather than an edge the colour of
  // its own fill. On the translucent panels these sit on (`.tq-panel` is
  // rgba(13,22,62,.72) over a photographic backdrop) a self-coloured border is
  // no border at all: the button's shape dissolves into whatever happens to be
  // behind it, and a row of them reads as floating text. The trim is what makes
  // each one legible as a discrete control.
  const palette = {
    primary: { background: "var(--blue)", color: "#fff", border: "rgba(255,255,255,.85)" },
    // A white button is a light chip wherever it lands, so its ink and its edge
    // are stated rather than inherited — the dark console retints both tokens.
    // It needs no trim: it is already the lightest thing on the surface.
    secondary: { background: "#fff", color: "#0e2c72", border: "#d7ddec" },
    danger: { background: "var(--red)", color: "#fff", border: "rgba(255,255,255,.85)" },
    // Ghost was navy ink on a transparent ground, which dates from a light
    // surface this application no longer has — every panel and every screen is
    // dark now, so it rendered as near-invisible text. Light ink and a faint
    // trim make it what it was always meant to be: the quiet option, still an
    // obvious button.
    ghost: { background: "transparent", color: "#e8efff", border: "rgba(255,255,255,.45)" },
  }[variant];

  const sizing = {
    sm: { padding: "7px 12px", fontSize: 13, minHeight: 34 },
    // §29.1 — a touch target is never smaller than 44 px.
    md: { padding: "10px 16px", fontSize: 14, minHeight: 44 },
    lg: { padding: "16px 22px", fontSize: 17, minHeight: 56 },
  }[size];

  // Disabled is a different button, not a faded one. Fading the fill to 55%
  // leaves a saturated blue rectangle with grey text on it, which reads as poor
  // contrast — a thing that is broken — rather than as a control that is not
  // available yet. Draining the fill entirely and keeping a visible edge says
  // "this is a button, and it is waiting for something" at a glance.
  const off = rest.disabled
    ? {
        background: "rgba(255,255,255,.08)",
        color: "rgba(255,255,255,.45)",
        border: "2px solid rgba(255,255,255,.20)",
      }
    : null;

  return (
    <button
      type="button"
      {...rest}
      style={{
        ...sizing,
        background: palette.background,
        color: palette.color,
        border: `2px solid ${palette.border}`,
        borderRadius: "var(--radius-sm)",
        fontWeight: 800,
        fontFamily: "inherit",
        cursor: rest.disabled ? "not-allowed" : "pointer",
        ...off,
        // A caller's own style still wins, disabled or not.
        ...style,
      }}
    />
  );
}

export interface BrandLockupProps {
  /** §35.6 — the tenant's three lines, from the session snapshot. */
  brand: { top: string; main: string; accent: string } | null | undefined;
  /** Scales the whole lockup; the display uses a larger one than a phone. */
  scale?: number;
}

/**
 * The three-line brand lockup from docs/ux-mockup.html — small tinted line,
 * then two heavy lines with the second in green. The display, the player device,
 * and the facilitator console all draw the same one, which is what makes the
 * three surfaces read as one product in a room.
 */
/** The lockup a host that predates §35.6 branding still renders. */
const FALLBACK_LOCKUP = { top: "FPPTA", main: "TRUSTEE", accent: "SHOWDOWN" };

export function BrandLockup({ brand, scale = 1 }: BrandLockupProps): React.ReactElement {
  // A snapshot from an older server has no lockup at all, and a missing brand is
  // never a reason to take a player's screen down.
  const lines = brand ?? FALLBACK_LOCKUP;
  return (
    <div className="tq-lockup" style={{ fontSize: `${scale}em` }}>
      <span className="tq-lockup-top">{lines.top}</span>
      <span className="tq-lockup-main">{lines.main}</span>
      <span className="tq-lockup-accent">{lines.accent}</span>
    </div>
  );
}
